import { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogPanel,
} from "@/client/components/ui/dialog";
import { Button } from "@/client/components/ui/button";
import { Input } from "@/client/components/ui/input";
import { Label } from "@/client/components/ui/label";
import { Notice } from "@/client/components/ui/notice";
import {
  AlertCircleIcon,
  PencilIcon,
  Trash2Icon,
  PlusIcon,
  KeyRoundIcon,
} from "lucide-react";

/**
 * KUVO-only management of a school's kunstcoördinatoren, opened as a dialog from a row in the
 * Scholen table. A school can have several coordinators (each with their own login). KUVO owns
 * this roster, the school may not add/remove coordinators.
 *
 * Wired to the admin endpoints under /api/admin/schools/:schoolId/coordinators via raw fetch
 * (consistent with the school-side ContactpersonenSection; the two routers share the
 * /admin/schools mount point so the RPC client can't cleanly disambiguate :schoolId vs :id).
 *
 * The copy makes the login consequence explicit: adding a coordinator creates a login for that
 * e-mail, removing one revokes it.
 */

type Coordinator = {
  personId: number;
  name: string;
  givenName: string | null;
  familyName: string | null;
  email: string | null;
  workDays: string[];
  emergencyPhone: string | null;
};

type CoordinatorForm = { givenName: string; familyName: string; email: string };
const emptyForm: CoordinatorForm = { givenName: "", familyName: "", email: "" };

const WERKDAG_LABELS: Record<string, string> = {
  monday: "Ma",
  tuesday: "Di",
  wednesday: "Wo",
  thursday: "Do",
  friday: "Vr",
};
const WERKDAG_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday"];
const formatWorkDays = (days: string[]) =>
  [...days]
    .sort((a, b) => WERKDAG_ORDER.indexOf(a) - WERKDAG_ORDER.indexOf(b))
    .map((d) => WERKDAG_LABELS[d] ?? d)
    .join(", ");

function validate(form: CoordinatorForm): Partial<Record<keyof CoordinatorForm, string>> {
  const e: Partial<Record<keyof CoordinatorForm, string>> = {};
  if (!form.givenName.trim()) e.givenName = "Vul een voornaam in.";
  if (!form.familyName.trim()) e.familyName = "Vul een achternaam in.";
  if (!form.email.trim()) e.email = "Vul een e-mailadres in.";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Ongeldig e-mailadres.";
  return e;
}

export function CoordinatorenDialog({
  schoolId,
  schoolName,
  open,
  onClose,
}: {
  schoolId: number | null;
  schoolName: string | null;
  open: boolean;
  onClose: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Kunstcoördinatoren</DialogTitle>
          <DialogDescription>
            {schoolName
              ? `Beheer de kunstcoördinatoren van ${schoolName}.`
              : "Beheer de kunstcoördinatoren van deze school."}
          </DialogDescription>
        </DialogHeader>
        <DialogPanel>
          {/* Keyed remount so the body reloads fresh state each time a school is opened. */}
          {open && schoolId != null && (
            <CoordinatorenBody key={schoolId} schoolId={schoolId} />
          )}
        </DialogPanel>
      </DialogContent>
    </Dialog>
  );
}

function CoordinatorenBody({ schoolId }: { schoolId: number }) {
  const [coordinators, setCoordinators] = useState<Coordinator[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Adding a new coordinator / editing an existing one (mutually exclusive).
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<CoordinatorForm>(emptyForm);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof CoordinatorForm, string>>>({});
  const [formMessage, setFormMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Delete confirmation.
  const [deleteTarget, setDeleteTarget] = useState<Coordinator | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setError(false);
      const res = await fetch(`/api/admin/schools/${schoolId}/coordinators`, { credentials: "include" });
      if (res.ok) {
        const d = await res.json();
        setCoordinators((d.coordinators ?? []) as Coordinator[]);
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    }
    setLoading(false);
  }, [schoolId]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setAdding(false);
    setEditingId(null);
    setForm(emptyForm);
    setFormErrors({});
    setFormMessage(null);
  };

  const startAdd = () => {
    setEditingId(null);
    setAdding(true);
    setForm(emptyForm);
    setFormErrors({});
    setFormMessage(null);
  };

  const startEdit = (c: Coordinator) => {
    setAdding(false);
    setEditingId(c.personId);
    setForm({ givenName: c.givenName ?? "", familyName: c.familyName ?? "", email: c.email ?? "" });
    setFormErrors({});
    setFormMessage(null);
  };

  const handleCreate = async () => {
    const errs = validate(form);
    setFormErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSaving(true);
    setFormMessage(null);
    try {
      const res = await fetch(`/api/admin/schools/${schoolId}/coordinators`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          givenName: form.givenName.trim(),
          familyName: form.familyName.trim(),
          email: form.email.trim(),
        }),
      });
      if (res.ok) {
        resetForm();
        await load();
      } else {
        setFormMessage("Toevoegen is niet gelukt. Probeer het later opnieuw.");
      }
    } catch {
      setFormMessage("Kan geen verbinding maken met de server. Probeer het opnieuw.");
    }
    setSaving(false);
  };

  const handleUpdate = async () => {
    if (editingId == null) return;
    const errs = validate(form);
    setFormErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSaving(true);
    setFormMessage(null);
    try {
      const res = await fetch(`/api/admin/schools/${schoolId}/coordinators/${editingId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          givenName: form.givenName.trim(),
          familyName: form.familyName.trim(),
          email: form.email.trim(),
        }),
      });
      if (res.ok) {
        resetForm();
        await load();
      } else {
        const err = await res.json().catch(() => ({}));
        if ((err as any).error === "email_taken") {
          setFormMessage("Dit e-mailadres is al in gebruik door iemand anders. Kies een ander adres.");
        } else {
          setFormMessage("Wijzigen is niet gelukt. Probeer het later opnieuw.");
        }
      }
    } catch {
      setFormMessage("Kan geen verbinding maken met de server. Probeer het opnieuw.");
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    setDeleteError(null);
    try {
      const res = await fetch(`/api/admin/schools/${schoolId}/coordinators/${deleteTarget.personId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        setDeleteTarget(null);
        await load();
      } else {
        setDeleteError("Verwijderen is niet gelukt. Probeer het later opnieuw.");
      }
    } catch {
      setDeleteError("Kan geen verbinding maken met de server. Probeer het opnieuw.");
    }
    setSaving(false);
  };

  if (loading) return <p className="text-sm text-muted-foreground">Laden...</p>;

  if (error) {
    return (
      <Notice
        tone="error"
        icon={<AlertCircleIcon />}
        title="Kan de coördinatoren niet laden"
        action={
          <Button size="sm" variant="outline" onClick={() => { setLoading(true); load(); }}>
            Opnieuw proberen
          </Button>
        }
      >
        Controleer de internetverbinding en probeer het opnieuw.
      </Notice>
    );
  }

  const formOpen = adding || editingId != null;

  return (
    <div className="space-y-4">
      <Notice tone="info" icon={<KeyRoundIcon />}>
        Elke coördinator krijgt een eigen inlog op het e-mailadres dat u hier invult. Een coördinator
        toevoegen maakt die inlog aan; verwijderen trekt de inlog weer in. De school kan coördinatoren
        zelf niet toevoegen of verwijderen.
      </Notice>

      {coordinators.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Deze school heeft nog geen kunstcoördinator. Voeg de eerste hieronder toe.
        </p>
      ) : (
        <div className="space-y-2">
          {coordinators.map((c) => {
            const editing = editingId === c.personId;
            if (editing) {
              return (
                <div key={c.personId} className="rounded-lg border border-primary/30 bg-card p-4">
                  <CoordinatorFormFields
                    form={form}
                    formErrors={formErrors}
                    formMessage={formMessage}
                    saving={saving}
                    onChange={setForm}
                    onSave={handleUpdate}
                    onCancel={resetForm}
                    saveLabel="Opslaan"
                  />
                </div>
              );
            }
            return (
              <CoordinatorCard
                key={c.personId}
                coordinator={c}
                disabled={formOpen}
                onEdit={() => startEdit(c)}
                onDelete={() => { setDeleteTarget(c); setDeleteError(null); }}
              />
            );
          })}
        </div>
      )}

      {/* Add form / Add button */}
      {adding ? (
        <div className="rounded-lg border border-border bg-card p-4">
          <p className="mb-3 text-sm font-medium text-foreground">Nieuwe coördinator</p>
          <CoordinatorFormFields
            form={form}
            formErrors={formErrors}
            formMessage={formMessage}
            saving={saving}
            onChange={setForm}
            onSave={handleCreate}
            onCancel={resetForm}
            saveLabel="Toevoegen en inlog aanmaken"
          />
        </div>
      ) : (
        !editingId && (
          <button
            type="button"
            className="flex w-full cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border px-4 py-3 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
            onClick={startAdd}
          >
            <PlusIcon size={14} />
            Coördinator toevoegen
          </button>
        )
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={deleteTarget !== null} onOpenChange={(o) => { if (!o) { setDeleteTarget(null); setDeleteError(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Coördinator verwijderen?</DialogTitle>
            <DialogDescription>
              U verwijdert {deleteTarget?.name} als kunstcoördinator van deze school. Hiermee trekt u
              ook de inlog van deze persoon in; daarna heeft die geen toegang meer tot het portaal.
            </DialogDescription>
          </DialogHeader>
          {deleteError && (
            <div className="px-6 pb-1">
              <p className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                {deleteError}
              </p>
            </div>
          )}
          <div className="flex flex-col-reverse gap-2 border-t bg-muted/72 px-6 py-4 sm:flex-row sm:justify-end sm:rounded-b-[calc(var(--radius-2xl)-1px)]">
            <Button variant="outline" onClick={() => { setDeleteTarget(null); setDeleteError(null); }} disabled={saving}>
              Annuleren
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={saving}>
              {saving ? "Verwijderen..." : "Verwijderen en inlog intrekken"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Coordinator card ───

function CoordinatorCard({
  coordinator,
  disabled,
  onEdit,
  onDelete,
}: {
  coordinator: Coordinator;
  disabled: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const c = coordinator;
  return (
    <div className="rounded-lg border border-border bg-card px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">{c.name}</p>
          {c.email && <p className="text-sm text-muted-foreground">{c.email}</p>}
          {c.workDays.length > 0 && (
            <p className="text-xs text-muted-foreground">Werkdagen: {formatWorkDays(c.workDays)}</p>
          )}
          {c.emergencyPhone && (
            <p className="text-xs text-muted-foreground">Nood: {c.emergencyPhone}</p>
          )}
        </div>
        <div className="flex shrink-0 gap-2">
          <Button variant="outline" size="sm" onClick={onEdit} disabled={disabled}>
            <PencilIcon /> Wijzigen
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onDelete}
            disabled={disabled}
            className="text-muted-foreground hover:text-destructive"
          >
            <Trash2Icon /> Verwijderen
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Coordinator form fields ───

function CoordinatorFormFields({
  form,
  formErrors,
  formMessage,
  saving,
  onChange,
  onSave,
  onCancel,
  saveLabel,
}: {
  form: CoordinatorForm;
  formErrors: Partial<Record<keyof CoordinatorForm, string>>;
  formMessage: string | null;
  saving: boolean;
  onChange: (f: CoordinatorForm) => void;
  onSave: () => void;
  onCancel: () => void;
  saveLabel: string;
}) {
  const set = (key: keyof CoordinatorForm, val: string) => onChange({ ...form, [key]: val });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <Label className="text-xs">Voornaam <span className="text-red-600">*</span></Label>
          <Input value={form.givenName} onChange={(e) => set("givenName", e.target.value)} placeholder="Voornaam" className="mt-1" />
          {formErrors.givenName && <p className="mt-1 text-xs text-red-600">{formErrors.givenName}</p>}
        </div>
        <div>
          <Label className="text-xs">Achternaam <span className="text-red-600">*</span></Label>
          <Input value={form.familyName} onChange={(e) => set("familyName", e.target.value)} placeholder="Achternaam" className="mt-1" />
          {formErrors.familyName && <p className="mt-1 text-xs text-red-600">{formErrors.familyName}</p>}
        </div>
      </div>
      <div>
        <Label className="text-xs">E-mailadres <span className="text-red-600">*</span></Label>
        <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="naam@school.nl" className="mt-1" />
        <p className="mt-1 text-xs text-muted-foreground">
          Dit e-mailadres wordt de inlog van de coördinator voor het portaal.
        </p>
        {formErrors.email && <p className="mt-1 text-xs text-red-600">{formErrors.email}</p>}
      </div>
      {formMessage && <p className="text-sm text-destructive">{formMessage}</p>}
      <div className="flex gap-2 pt-1">
        <Button size="sm" onClick={onSave} disabled={saving}>
          {saving ? "Opslaan..." : saveLabel}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel} disabled={saving}>
          Annuleren
        </Button>
      </div>
    </div>
  );
}
